import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  FEATURE_REGISTRY,
  FeatureDef,
  FeatureConfigDoc,
  MergedFeatureConfig,
  SubscriptionTier,
  FeatureCategory,
  FeatureStatus,
} from '@everyday-os/shared';

function mergeConfig(registryEntry: FeatureDef, doc: FeatureConfigDoc | null): MergedFeatureConfig {
  return {
    ...registryEntry,
    featureCategory: (doc?.featureCategory ?? 'core') as FeatureCategory,
    status: (doc?.status ?? 'active') as FeatureStatus,
    maintenanceMessage: doc?.maintenanceMessage,
    disabledRedirectPath: doc?.disabledRedirectPath,
    effectiveMinTier: (doc?.minTierOverride ?? registryEntry.minTier ?? SubscriptionTier.FREE) as SubscriptionTier,
    effectiveScopes: doc?.allowedScopes ?? ['personal', 'household'],
    addonIncludedInTiers: (doc?.addonIncludedInTiers ?? []) as SubscriptionTier[],
    rolloutPercent: doc?.rolloutPercent ?? 100,
    rolloutByTier: (doc?.rolloutByTier ?? {}) as Partial<Record<SubscriptionTier, number>>,
    rolloutHouseholdIds: doc?.rolloutHouseholdIds ?? [],
    scheduledChanges: doc?.scheduledChanges ?? [],
    lastModifiedBy: doc?.lastModifiedBy,
    lastModifiedAt: doc?.lastModifiedAt,
    modificationReason: doc?.modificationReason,
  };
}

interface FeatureConfigContextValue {
  configs: Map<string, MergedFeatureConfig>;
  isLoaded: boolean;
  isError: boolean;
}

const defaultConfigs = (): Map<string, MergedFeatureConfig> => {
  const map = new Map<string, MergedFeatureConfig>();
  FEATURE_REGISTRY.forEach((entry: FeatureDef) => map.set(entry.id, mergeConfig(entry, null)));
  return map;
};

const FeatureConfigContext = createContext<FeatureConfigContextValue>({
  configs: defaultConfigs(),
  isLoaded: false,
  isError: false,
});

export const FeatureConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [configs, setConfigs] = useState<Map<string, MergedFeatureConfig>>(defaultConfigs);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'featureConfig'))
      .then((snapshot) => {
        const overrideMap = new Map<string, FeatureConfigDoc>();
        snapshot.docs.forEach(d => overrideMap.set(d.id, d.data() as FeatureConfigDoc));

        const merged = new Map<string, MergedFeatureConfig>();
        FEATURE_REGISTRY.forEach((entry: FeatureDef) => {
          merged.set(entry.id, mergeConfig(entry, overrideMap.get(entry.id) ?? null));
        });

        setConfigs(merged);
        setIsLoaded(true);
        setIsError(false);
      })
      .catch((error) => {
        console.error('[FeatureConfigProvider] Firestore error, using defaults:', error);
        setIsLoaded(true);
        setIsError(true);
      });
  }, []);

  return (
    <FeatureConfigContext.Provider value={{ configs, isLoaded, isError }}>
      {children}
    </FeatureConfigContext.Provider>
  );
};

export function useFeatureConfigContext(): FeatureConfigContextValue {
  return useContext(FeatureConfigContext);
}
